<template>
  <div class="bg-white rounded-xl shadow-sm p-6">
    <div class="flex items-center justify-between mb-4 flex-wrap gap-3">
      <h2 class="text-lg font-bold text-neutral-900">案例审核</h2>
      <div class="flex items-center gap-2">
        <select
          v-model="status"
          class="border border-neutral-200 rounded-lg px-3 py-1.5 text-sm outline-none"
          @change="getList"
        >
          <option :value="-1">全部状态</option>
          <option :value="0">待审核</option>
          <option :value="1">已通过</option>
          <option :value="2">已驳回</option>
        </select>
        <input
          v-model="searchKey"
          placeholder="搜索主题 / 作者"
          class="border border-neutral-200 rounded-lg px-3 py-1.5 text-sm outline-none"
          @input="handleSearch"
        />
      </div>
    </div>

    <div v-if="loading" class="text-center py-12 text-neutral-400 text-sm">
      加载中…
    </div>
    <div v-else-if="list.length === 0" class="text-center py-12 text-neutral-400 text-sm">
      暂无数据
    </div>

    <div v-else class="divide-y divide-neutral-100">
      <div v-for="c in list" :key="c.id" class="py-4 flex gap-4">
        <div class="w-20 h-20 rounded-lg bg-neutral-100 overflow-hidden flex-shrink-0">
          <img
            v-if="c.imgCount > 0"
            :src="imgUrl(c, 0)"
            class="w-full h-full object-cover"
            alt=""
          />
        </div>

        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-1 flex-wrap">
            <h3 class="font-medium text-neutral-900 truncate">{{ c.title }}</h3>
            <span class="text-xs px-2 py-0.5 rounded" :class="statusClass(c.status)">{{
              statusText(c.status)
            }}</span>
            <span
              v-if="c.awardLevel || c.awardRank"
              class="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-600"
            >
              <i class="fa fa-trophy mr-1"></i>{{ [c.awardLevel, c.awardRank].filter(Boolean).join("") }}
            </span>
          </div>
          <div class="text-xs text-neutral-500 mb-1">
            {{ c.author }} · {{ fmtTime(c.createTime) }} · {{ c.scenario }}
          </div>
          <p class="text-sm text-neutral-600 case-ellipsis">{{ c.content }}</p>
          <p v-if="c.status === 2 && c.examInfo" class="text-xs text-red-500 mt-1">
            驳回理由：{{ c.examInfo }}
          </p>
        </div>

        <div class="flex flex-col gap-2 justify-center flex-shrink-0">
          <button
            class="text-xs px-3 py-1 rounded bg-neutral-100 hover:bg-neutral-200"
            @click="openDetail(c)"
          >
            查看
          </button>
          <button
            v-if="c.status !== 1"
            class="text-xs px-3 py-1 rounded bg-green-500 text-white hover:bg-green-600"
            @click="review(c, 1)"
          >
            通过
          </button>
          <button
            v-if="c.status !== 2"
            class="text-xs px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600"
            @click="review(c, 2)"
          >
            驳回
          </button>
          <button
            class="text-xs px-3 py-1 rounded bg-neutral-100 hover:bg-neutral-200 text-red-500"
            @click="removeCase(c)"
          >
            删除
          </button>
        </div>
      </div>
    </div>

    <div class="flex justify-center mt-6" v-if="total > pageSize">
      <el-pagination
        background
        layout="prev, pager, next"
        :total="total"
        :page-size="pageSize"
        :current-page="page"
        @current-change="handlePageChange"
      />
    </div>

    <!-- 详情 -->
    <el-dialog v-model="detailVisible" :title="detail ? detail.title : ''" width="720px" top="6vh">
      <div v-if="detail">
        <div class="grid grid-cols-3 gap-y-2 text-sm mb-4">
          <div class="text-neutral-500">作者</div>
          <div class="col-span-2">{{ detail.author }}</div>
          <div class="text-neutral-500">发布时间</div>
          <div class="col-span-2">{{ fmtTime(detail.createTime) }}</div>
          <div class="text-neutral-500">应用场景</div>
          <div class="col-span-2">{{ detail.scenario }}</div>
          <div class="text-neutral-500">案例关键词</div>
          <div class="col-span-2">{{ detail.keywords || "—" }}</div>
          <div class="text-neutral-500">获奖等级</div>
          <div class="col-span-2">
            {{ [detail.awardLevel, detail.awardRank].filter(Boolean).join("") || "—" }}
          </div>
          <div class="text-neutral-500">状态</div>
          <div class="col-span-2">
            <span class="text-xs px-2 py-0.5 rounded" :class="statusClass(detail.status)">{{
              statusText(detail.status)
            }}</span>
            <span v-if="detail.examor" class="text-xs text-neutral-400 ml-2"
              >审核人：{{ detail.examor }}</span
            >
          </div>
        </div>

        <div v-if="detail.imgCount > 0" class="mb-4">
          <div class="text-sm text-neutral-500 mb-2">核心图示</div>
          <div class="grid grid-cols-3 gap-2">
            <img
              v-for="n in detail.imgCount"
              :key="n"
              :src="imgUrl(detail, n - 1)"
              class="w-full h-28 object-cover rounded-lg"
              alt=""
            />
          </div>
        </div>

        <div>
          <div class="text-sm text-neutral-500 mb-2">主要内容</div>
          <p class="text-sm text-neutral-800 leading-7 whitespace-pre-wrap">
            {{ detail.content }}
          </p>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";

export default {
  name: "CaseManager",
  data() {
    return {
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      loading: false,
      status: 0,
      searchKey: "",
      searchTimer: null,
      detail: null,
      detailVisible: false,
    };
  },
  created() {
    this.getList();
  },
  methods: {
    async getList() {
      this.loading = true;
      try {
        const data = await this.$aesEncrypt({
          page: this.page,
          pageSize: this.pageSize,
          status: this.status,
          searchKey: this.searchKey,
        });
        const res = await this.$axios({
          url: "case/admin/list",
          method: "POST",
          data: { data },
        });
        if (res.code === 200) {
          this.list = res.data.list || [];
          this.total = res.data.total || 0;
        } else {
          ElMessage.error(res.msg || "加载失败");
        }
      } catch (e) {
        ElMessage.error((e && e.message) || "加载失败");
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.getList();
      }, 300);
    },
    handlePageChange(p) {
      this.page = p;
      this.getList();
    },
    openDetail(c) {
      this.detail = c;
      this.detailVisible = true;
    },
    review(c, act) {
      const pass = act === 1;
      const doIt = (reason) => {
        this.doReview(c.id, act, reason);
      };
      if (pass) {
        ElMessageBox.confirm(`确定通过案例「${c.title}」吗？通过后将在案例广场公开。`, "审核通过", {
          confirmButtonText: "通过",
          cancelButtonText: "取消",
          type: "success",
        })
          .then(() => doIt(""))
          .catch(() => {});
      } else {
        ElMessageBox.prompt("请输入驳回理由（会展示给投稿人）", "驳回案例", {
          confirmButtonText: "驳回",
          cancelButtonText: "取消",
          inputPattern: /\S/,
          inputErrorMessage: "理由不能为空",
        })
          .then(({ value }) => doIt(value))
          .catch(() => {});
      }
    },
    async doReview(id, status, examInfo) {
      try {
        const data = await this.$aesEncrypt({ id, status, examInfo });
        const res = await this.$axios({ url: "case/exam", method: "PUT", data: { data } });
        if (res.code === 200) {
          ElMessage.success(res.data || "操作成功");
          this.getList();
        } else {
          ElMessage.error(res.msg || "操作失败");
        }
      } catch (e) {
        ElMessage.error((e && e.message) || "操作失败");
      }
    },
    removeCase(c) {
      ElMessageBox.confirm(`确定删除案例「${c.title}」吗？删除后不可恢复。`, "删除确认", {
        confirmButtonText: "删除",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(async () => {
          const res = await this.$axios({ url: "case/admin/" + c.id, method: "DELETE" });
          if (res.code === 200) {
            ElMessage.success("已删除");
            this.getList();
          } else {
            ElMessage.error(res.msg || "删除失败");
          }
        })
        .catch(() => {});
    },
    imgUrl(c, i) {
      return process.env.VUE_APP_API_ROOT + "/case/image?id=" + c.id + "&i=" + i;
    },
    statusText(s) {
      return ["待审核", "已通过", "已驳回"][s] || "";
    },
    statusClass(s) {
      return (
        ["bg-amber-50 text-amber-600", "bg-green-50 text-green-600", "bg-red-50 text-red-600"][
          s
        ] || ""
      );
    },
    fmtTime(t) {
      return t ? String(t).replace("T", " ").substring(0, 16) : "";
    },
  },
};
</script>

<style scoped>
.case-ellipsis {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
